import type { TenantRenameErrorCode } from "@repo/shared/types";

// Local translations
export const T = {
  en_GB: {
    title: "Change address",
    description: "Choose a new web address for your organization.",
    newAddress: "New address",
    slugHelp: "Lowercase letters, numbers and hyphens.",
    whatHappens: "What happens",
    effectRedirect: (old: string, days: string) =>
      `The current address “${old}” keeps redirecting to the new one for ${days} days. After that it no longer works and can be taken by another organization.`,
    effectSso: (days: string) =>
      `Single sign-on keeps using the current address until an administrator has added the new callback URL in the identity provider and confirmed it on the SSO settings page. After ${days} days it switches automatically, and sign-in stops working if the identity provider has not been updated.`,
    effectUsers: "Everyone who is signed in is moved to the new address at their next page load.",
    effectIntegrations:
      "Integrations and bookmarks that store the address should be updated to the new one.",
    effectCooldown: (days: string) => `The address can then not be changed again for ${days} days.`,
    confirm: "Type the new address to confirm",
    cancel: "Cancel",
    saving: "Changing...",
    submit: "Change address",
    failed: "Failed to change the address",
    errors: {
      name_invalid: "The organization name is invalid.",
      slug_invalid:
        "Use 1-63 lowercase letters, numbers or hyphens, not starting or ending with a hyphen.",
      slug_reserved: "This address is reserved and cannot be used.",
      slug_taken: "This address is already used by another organization.",
      slug_quarantined:
        "This address was used by another organization recently and cannot be used yet.",
      slug_change_cooldown: "The address was changed recently and cannot be changed again yet.",
      tenant_not_editable: "This organization's address cannot be changed.",
    } satisfies Record<TenantRenameErrorCode, string>,
  },
  nb_NO: {
    title: "Endre adresse",
    description: "Velg en ny nettadresse for organisasjonen.",
    newAddress: "Ny adresse",
    slugHelp: "Små bokstaver, tall og bindestreker.",
    whatHappens: "Dette skjer",
    effectRedirect: (old: string, days: string) =>
      `Den nåværende adressen «${old}» videresender til den nye i ${days} dager. Deretter virker den ikke lenger og kan tas av en annen organisasjon.`,
    effectSso: (days: string) =>
      `Enkel pålogging bruker den nåværende adressen til en administrator har lagt inn den nye callback-URLen hos identitetsleverandøren og bekreftet på SSO-innstillingssiden. Etter ${days} dager byttes den automatisk, og innloggingen slutter å virke hvis identitetsleverandøren ikke er oppdatert.`,
    effectUsers: "Alle som er logget inn flyttes til den nye adressen ved neste sidelasting.",
    effectIntegrations:
      "Integrasjoner og bokmerker som lagrer adressen bør oppdateres til den nye.",
    effectCooldown: (days: string) => `Adressen kan deretter ikke endres igjen på ${days} dager.`,
    confirm: "Skriv den nye adressen for å bekrefte",
    cancel: "Avbryt",
    saving: "Endrer...",
    submit: "Endre adresse",
    failed: "Kunne ikke endre adressen",
    errors: {
      name_invalid: "Organisasjonsnavnet er ugyldig.",
      slug_invalid:
        "Bruk 1–63 små bokstaver, tall eller bindestreker, uten bindestrek først eller sist.",
      slug_reserved: "Denne adressen er reservert og kan ikke brukes.",
      slug_taken: "Denne adressen brukes allerede av en annen organisasjon.",
      slug_quarantined:
        "Denne adressen ble nylig brukt av en annen organisasjon og kan ikke brukes ennå.",
      slug_change_cooldown: "Adressen ble nylig endret og kan ikke endres igjen ennå.",
      tenant_not_editable: "Denne organisasjonens adresse kan ikke endres.",
    } satisfies Record<TenantRenameErrorCode, string>,
  },
} as const;
